import { useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Seo from "@/components/base/Seo";
import { comics } from "@/mocks/comics";
import { readingPages } from "@/mocks/readingPages";

const siteUrl = "https://teachmanga4.github.io";

export default function ReadPage() {
  const { id = "", chapterId = "" } = useParams();
  const navigate = useNavigate();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  const chapterIndex = useMemo(() => {
    if (!comic) return -1;
    return comic.chapters.findIndex((ch) => ch.id === chapterId);
  }, [comic, chapterId]);

  if (!comic || chapterIndex < 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background-50 px-4">
        <i className="ri-article-line text-4xl text-foreground-500" aria-hidden="true" />
        <p className="text-sm text-foreground-700">章节不存在或已被删除</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/category/all" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50">
            返回全部漫画
          </Link>
          {comic && (
            <Link to={`/manga/${comic.id}`} className="rounded-md bg-background-100 px-5 py-2.5 text-sm font-bold text-foreground-950">
              返回漫画目录
            </Link>
          )}
        </div>
      </div>
    );
  }

  const chapter = comic.chapters[chapterIndex];
  const prevChapter = chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : null;
  const nextChapter = chapterIndex < comic.chapters.length - 1 ? comic.chapters[chapterIndex + 1] : null;

  const pages = Array.from(
    { length: Math.min(chapter.pageCount, readingPages.length) },
    (_, i) => readingPages[(chapterIndex + i) % readingPages.length]
  );

  const goToChapter = (chId: string) => {
    navigate(`/read/${comic.id}/${chId}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Seo
        title={`${comic.title} ${chapter.title}-下拉式漫画阅读-178漫画网`}
        description={`在线免费阅读《${comic.title}》第${chapterIndex + 1}话 ${chapter.title}，下拉式漫画阅读方式，便捷的浏览方式解放您的双手。`}
        keywords="下拉式漫画,在线免费阅读,178漫画网,漫画阅读"
        canonical={`${siteUrl}/read/${comic.id}/${chapter.id}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
              { "@type": "ListItem", position: 2, name: comic.title, item: `${siteUrl}/manga/${comic.id}` },
              { "@type": "ListItem", position: 3, name: chapter.title, item: `${siteUrl}/read/${comic.id}/${chapter.id}` },
            ],
          },
        ]}
      />

      {/* Top reader bar */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur-md">
        <div className="flex h-14 items-center justify-between gap-3 px-3 md:h-16 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50" aria-label="返回178漫画网首页">
              <i className="ri-fire-fill" aria-hidden="true" />
            </Link>
            <Link to={`/manga/${comic.id}`} className="hidden min-w-0 items-center gap-2 text-sm font-semibold text-foreground-950 transition-colors hover:text-primary-600 sm:flex">
              <span className="truncate">{comic.title}</span>
            </Link>
            <span className="hidden text-foreground-400 sm:inline">/</span>
            <span className="truncate text-sm text-foreground-700">{chapter.title}</span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <select
                value={chapter.id}
                onChange={(e) => goToChapter(e.target.value)}
                aria-label="选择章节"
                className="h-10 max-w-[160px] cursor-pointer appearance-none rounded-md border border-background-200 bg-background-100 px-3 pr-8 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-300 md:max-w-[220px]"
              >
                {comic.chapters.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.title}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-600" aria-hidden="true" />
            </div>
            <Link
              to={`/manga/${comic.id}`}
              className="hidden h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-background-100 px-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200 md:flex"
            >
              <i className="ri-list-unordered" aria-hidden="true" />
              目录
            </Link>
          </div>
        </div>
      </header>

      {/* Reader body */}
      <main id="main-content" className="flex-1 pt-14 md:pt-16">
        <div className="mx-auto w-full max-w-3xl px-0 sm:px-4">
          <div className="flex flex-col items-center bg-background-50">
            <h1 className="w-full border-b border-background-200 px-4 py-6 text-center">
              <span className="block text-xs text-foreground-600">{comic.title}</span>
              <span className="mt-1.5 block font-heading text-lg font-black text-foreground-950 md:text-xl">{chapter.title}</span>
            </h1>

            {/* Reading pages */}
            <div className="flex w-full flex-col items-center gap-1">
              {pages.map((page, idx) => (
                <div key={`${chapter.id}-${page.id}-${idx}`} className="w-full overflow-hidden">
                  <img
                    src={page.url}
                    alt={`${comic.title} ${chapter.title} 第${idx + 1}页`}
                    title={`${comic.title} ${chapter.title} 第${idx + 1}页-178漫画网`}
                    className="mx-auto w-full"
                  />
                </div>
              ))}
            </div>

            {/* End of chapter */}
            <div className="w-full px-4 py-8 text-center">
              <p className="text-xs text-foreground-600">本话完</p>
              <p className="mt-1 text-xs text-foreground-600">下拉式漫画阅读 · 感谢支持178漫画网</p>
            </div>
          </div>

          {/* Prev / Next */}
          <div className="flex flex-col gap-3 px-4 pb-10 pt-4 sm:flex-row sm:items-center sm:justify-between">
            {prevChapter ? (
              <Link
                to={`/read/${comic.id}/${prevChapter.id}`}
                className="flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 text-sm font-bold text-foreground-950 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" aria-hidden="true" />
                上一话
              </Link>
            ) : (
              <span className="flex h-11 flex-1 cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-100 px-4 text-sm text-foreground-500">
                <i className="ri-arrow-left-line" aria-hidden="true" />
                已是第一话
              </span>
            )}
            <Link
              to={`/manga/${comic.id}`}
              className="flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-100 px-4 text-sm font-bold text-foreground-950 transition-colors hover:bg-background-200"
            >
              <i className="ri-list-unordered" aria-hidden="true" />
              返回目录
            </Link>
            {nextChapter ? (
              <Link
                to={`/read/${comic.id}/${nextChapter.id}`}
                className="flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                下一话
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            ) : (
              <span className="flex h-11 flex-1 cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-200 px-4 text-sm text-foreground-500">
                已是最新一话
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}